import {CountryEmployeesView} from '@frontend/shared-api';
import {
  countryCoverage,
  expansionPotential,
  functionSubtotals,
  lostPercentagePoints,
} from '../rfi-weighted-coverage.calc';
import {CoverageCell, WeightedCoverage} from '../step-5-3-rfi-invitation-list.types';
import {
  CoverageAnalysisView,
  CoverageCalculationRow,
  CoverageCountryRow,
  CoverageExample,
  CoverageExpansion,
  CoverageGapRow,
  CoverageMatrixRow,
  CoverageNameLookup,
  CoverageSubtotalRow,
  MatrixCellStatus,
} from './rfi-coverage-analysis-dialog.types';

const LARGEST_GAP_COUNT = 5;
const CAUSE_KEY_PREFIX = 'project.steps.rfi.invitationList.analysis.cause.';
const FULL_COVERAGE_THRESHOLD = 90;
const PARTIAL_COVERAGE_THRESHOLD = 75;

export function buildCoverageAnalysisView(
  coverage: WeightedCoverage,
  countryEmployees: CountryEmployeesView[],
  names: CoverageNameLookup,
): CoverageAnalysisView {
  const gaps = coverage.gaps.map((cell) => gapRow(cell, coverage, names));
  const countries = countryRows(coverage, countryEmployees, names);

  return {
    score: coverage.score,
    possiblePoints: coverage.possiblePoints,
    achievedPoints: coverage.achievedPoints,
    missingPoints: coverage.possiblePoints - coverage.achievedPoints,
    coveredFunctionCount: coverage.coveredFunctionIds.length,
    requiredFunctionCount: coverage.requiredFunctionIds.length,
    supportedCountryCount: coverage.supportedCountryIsoCodes.length,
    requiredCountryCount: coverage.requiredCountryIsoCodes.length,
    gaps,
    largestGaps: gaps.slice(0, LARGEST_GAP_COUNT),
    countries,
    matrixRows: matrixRows(coverage, countries, names),
    subtotals: subtotalRows(coverage, names),
    calculationRows: coverage.cells.map((cell) => calculationRow(cell, names)),
    example: largestExample(coverage, names),
    expansion: expansion(coverage, names),
  };
}

export function causeKey(cell: CoverageCell): string {
  if (!cell.functionAvailable && !cell.countrySupported) {
    return `${CAUSE_KEY_PREFIX}functionAndCountry`;
  }
  return `${CAUSE_KEY_PREFIX}${cell.functionAvailable ? 'country' : 'function'}`;
}

export function coverageClass(coverage: number | null): string {
  if (coverage === null) {
    return '';
  }
  if (coverage === 100) {
    return 'text-green';
  }
  return coverage === 0 ? 'text-red' : 'text-yellow';
}

export function coverageBarClass(score: number): string {
  if (score >= FULL_COVERAGE_THRESHOLD) {
    return 'bg-emerald-500';
  }
  return score >= PARTIAL_COVERAGE_THRESHOLD ? 'bg-amber-400' : 'bg-red-400';
}

function gapRow(
  cell: CoverageCell,
  coverage: WeightedCoverage,
  names: CoverageNameLookup,
): CoverageGapRow {
  return {
    functionName: names.functionName(cell.moduleFunctionId),
    countryName: names.countryName(cell.countryIsoCode),
    employees: cell.employees,
    weightShare: cell.functionWeight,
    causeKey: causeKey(cell),
    impact: lostPercentagePoints(cell, coverage),
  };
}

function countryRows(
  coverage: WeightedCoverage,
  countryEmployees: CountryEmployeesView[],
  names: CoverageNameLookup,
): CoverageCountryRow[] {
  const maxEmployees = Math.max(0, ...countryEmployees.map((country) => country.employees));
  const gapCountries = new Set(coverage.gaps.map((gap) => gap.countryIsoCode));

  return [...countryEmployees]
    .sort((first, second) => second.employees - first.employees)
    .map((country) => {
      const countryScore = countryCoverage(coverage, country.countryIsoCode);
      return {
        isoCode: country.countryIsoCode,
        name: names.countryName(country.countryIsoCode),
        employees: country.employees,
        employeeShare: maxEmployees === 0 ? 0 : (country.employees / maxEmployees) * 100,
        coverage: countryScore,
        coverageClass: coverageClass(countryScore),
        hasGap: gapCountries.has(country.countryIsoCode),
      };
    });
}

function matrixRows(
  coverage: WeightedCoverage,
  countries: CoverageCountryRow[],
  names: CoverageNameLookup,
): CoverageMatrixRow[] {
  return coverage.requiredFunctionIds.map((moduleFunctionId) => {
    const functionCells = coverage.cells.filter(
      (cell) => cell.moduleFunctionId === moduleFunctionId,
    );
    return {
      functionName: names.functionName(moduleFunctionId),
      weightShare: functionCells[0]?.functionWeight ?? 0,
      cells: countries.map((country) => ({
        isoCode: country.isoCode,
        status: matrixStatus(functionCells.find((cell) => cell.countryIsoCode === country.isoCode)),
      })),
    };
  });
}

function matrixStatus(cell: CoverageCell | undefined): MatrixCellStatus {
  if (!cell) {
    return 'notRequired';
  }
  return cell.covered ? 'covered' : 'missing';
}

function subtotalRows(
  coverage: WeightedCoverage,
  names: CoverageNameLookup,
): CoverageSubtotalRow[] {
  return functionSubtotals(coverage).map((subtotal) => {
    const functionCells = coverage.cells.filter(
      (cell) => cell.moduleFunctionId === subtotal.moduleFunctionId,
    );
    return {
      functionName: names.functionName(subtotal.moduleFunctionId),
      weightShare: functionCells[0]?.functionWeight ?? 0,
      employees: functionCells.reduce((sum, cell) => sum + cell.employees, 0),
      possiblePoints: subtotal.possiblePoints,
      achievedPoints: subtotal.achievedPoints,
      missingPoints: subtotal.possiblePoints - subtotal.achievedPoints,
    };
  });
}

function calculationRow(cell: CoverageCell, names: CoverageNameLookup): CoverageCalculationRow {
  return {
    functionName: names.functionName(cell.moduleFunctionId),
    isoCode: cell.countryIsoCode,
    weightShare: cell.functionWeight,
    employees: cell.employees,
    possiblePoints: cell.points,
    achievedPoints: cell.covered ? cell.points : 0,
    covered: cell.covered,
  };
}

function largestExample(
  coverage: WeightedCoverage,
  names: CoverageNameLookup,
): CoverageExample | null {
  const largest = coverage.cells.reduce<CoverageCell | null>(
    (current, cell) => (current === null || cell.points > current.points ? cell : current),
    null,
  );
  if (largest === null) {
    return null;
  }
  return {
    functionName: names.functionName(largest.moduleFunctionId),
    weightShare: largest.functionWeight,
    countryName: names.countryName(largest.countryIsoCode),
    employees: largest.employees,
    points: largest.points,
  };
}

function expansion(coverage: WeightedCoverage, names: CoverageNameLookup): CoverageExpansion {
  const missingFunctionIds = coverage.requiredFunctionIds.filter(
    (id) => !coverage.coveredFunctionIds.includes(id),
  );
  return {
    percent: expansionPotential(coverage),
    requestedAvailableCount: coverage.coveredFunctionIds.length,
    requestedMissingCount: missingFunctionIds.length,
    additionalCount: coverage.additionalFunctionIds.length,
    availableCount: coverage.coveredFunctionIds.length + coverage.additionalFunctionIds.length,
    coveredFunctionNames: coverage.coveredFunctionIds.map(names.functionName),
    missingFunctionNames: missingFunctionIds.map(names.functionName),
    additionalFunctionNames: coverage.additionalFunctionIds.map(names.functionName),
  };
}
